import { useMutation, useQueryClient } from '@tanstack/react-query'

import { apiScoping } from '@/lib/api/scoping'
import { assessmentKeys } from '@/features/assessments/api/query-keys'
import { buildHubspotExternalReferences } from '@/features/assessments/utils/build-hubspot-external-references'
import { buildHubspotPlantExternalReferences } from '@/features/assessments/utils/build-hubspot-plant-external-references'

export type UpdateAssessmentInput = {
  id: string
  plantName?: string
  country?: string
  dealOwnerUserID?: string | null
  salesEngineerUserID?: string | null
  isAtex?: boolean
  hubspotDealId?: string | null
  // Flag on (multiPhase): when present, only the hubspotPlant entry is
  // patched; the hubspot deal reference is left untouched.
  hubspotPlantId?: string | null
  assessedAt?: string | null
  lastOnsiteAt?: string | null
  status?: 'pending' | 'inProgress' | 'completed'
}

async function updateAssessment({
  id,
  hubspotDealId,
  hubspotPlantId,
  ...rest
}: UpdateAssessmentInput): Promise<void> {
  const body: Record<string, unknown> = { ...rest }
  const externalRefs =
    hubspotPlantId !== undefined
      ? buildHubspotPlantExternalReferences(hubspotPlantId)
      : buildHubspotExternalReferences(hubspotDealId)
  if (externalRefs !== undefined) {
    body.externalReferences = externalRefs
  }
  await apiScoping.patch(`/api/v1/plant-assessment/${id}`, body)
}

export function useUpdateAssessment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateAssessment,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: assessmentKeys.lists() })
    }
  })
}
